import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";

type Props = {
  page: number; // 1-based
  pageCount: number;
  onPageChange: (p: number) => void;
};

/** Compact pager: 1 … 4 5 6 … 12, with prev/next. */
function buildPageList(page: number, pageCount: number): Array<number | "…"> {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const out: Array<number | "…"> = [1];
  const left = Math.max(2, page - 1);
  const right = Math.min(pageCount - 1, page + 1);
  if (left > 2) out.push("…");
  for (let p = left; p <= right; p++) out.push(p);
  if (right < pageCount - 1) out.push("…");
  out.push(pageCount);
  return out;
}

export function Pagination({ page, pageCount, onPageChange }: Props) {
  if (pageCount <= 1) return null;
  const pages = buildPageList(page, pageCount);
  const goPrev = () => page > 1 && onPageChange(page - 1);
  const goNext = () => page < pageCount && onPageChange(page + 1);
  return (
    <nav className="pagination" aria-label="Pagination">
      <button
        type="button"
        className="pagination-btn"
        onClick={goPrev}
        disabled={page <= 1}
        aria-label="Previous page"
      >
        <ChevronLeft size={16} />
      </button>
      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`gap-${i}`} className="pagination-gap" aria-hidden>
            <MoreHorizontal size={14} />
          </span>
        ) : (
          <button
            key={p}
            type="button"
            className={`pagination-btn pagination-num${
              p === page ? " is-active" : ""
            }`}
            onClick={() => onPageChange(p)}
            aria-current={p === page ? "page" : undefined}
            aria-label={`Page ${p}`}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        className="pagination-btn"
        onClick={goNext}
        disabled={page >= pageCount}
        aria-label="Next page"
      >
        <ChevronRight size={16} />
      </button>
    </nav>
  );
}
